import { Button, type ButtonProps } from "@mantine/core";
import { Link, type LinkProps } from "@tanstack/react-router";
import { forwardRef } from "react";
import { useAppearance } from "../../features/customization/hooks/use-widget-config";

interface NavigationButtonProps
  extends Omit<ButtonProps, "component" | "href" | "onClick">,
    Pick<LinkProps, "to" | "search" | "params" | "preload"> {
  /** The route to navigate to */
  to: LinkProps["to"];
  /** Search parameters for the route */
  search?: LinkProps["search"];
  /** Route parameters */
  params?: LinkProps["params"];
  /** Preload behavior for the route */
  preload?: LinkProps["preload"];
  /** Optional onClick handler that will be called before navigation */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * A navigation button that uses Mantine's Button component with component="a"
 * and TanStack Router's Link for proper navigation and preloading.
 *
 * This component helps with preloading through TanStack Router by using
 * the Link component as the underlying implementation while maintaining
 * the Button's appearance and behavior.
 *
 * The button is hidden when showNavigation config is false.
 */
export const NavigationButton = forwardRef<HTMLAnchorElement, NavigationButtonProps>(
  ({ to, search, params, preload, onClick, children, ...buttonProps }, ref) => {
    const { appearance } = useAppearance();

    // Hide button if navigation is disabled via config
    if (appearance.showNavigation === false) {
      return null;
    }

    return (
      <Button
        component={Link}
        to={to}
        // biome-ignore lint/suspicious/noExplicitAny: We need to cast to any to avoid type errors
        search={search as any}
        // biome-ignore lint/suspicious/noExplicitAny: We need to cast to any to avoid type errors
        params={params as any}
        preload={preload}
        onClick={onClick}
        ref={ref}
        {...buttonProps}
      >
        {children}
      </Button>
    );
  },
);

NavigationButton.displayName = "NavigationButton";
